import React, { useEffect, useState, useMemo, useCallback } from 'react';
import SearchableSelect from '../shared/SearchableSelect';

const API_BASE_URL = process.env.REACT_APP_API_URL;
const getToken = () => localStorage.getItem('dths_token') || localStorage.getItem('token');
const authHeaders = () => ({
  'Authorization': `Bearer ${getToken()}`,
  'Content-Type': 'application/json',
});

const C = {
  navy:   '#0D1B2A',
  navy2:  '#142333',
  navy3:  '#1A2D42',
  teal:   '#00C8D4',
  teal2:  '#00A8B4',
  bg:     '#F0F4F8',
  card:   '#FFFFFF',
  text:   '#1E293B',
  muted:  '#64748B',
  border: '#E2E8F0',
  ok:     '#10B981',
  warn:   '#F59E0B',
  danger: '#EF4444',
  info:   '#3B82F6',
};

// Paleta cíclica para las columnas de período — ya no hay un número fijo de
// períodos (antes: solo 2023-24/2024-25/2025-26 quemados a mano), así que se
// asigna color por posición en vez de por año específico.
const PERIOD_PALETTE = [
  { color: '#1D4ED8', bg: '#EFF6FF' },
  { color: '#065F46', bg: '#ECFDF5' },
  { color: '#00A8B4', bg: 'rgba(0,200,212,.08)' },
  { color: '#B45309', bg: '#FFFBEB' },
  { color: '#7C3AED', bg: '#F5F3FF' },
  { color: '#BE185D', bg: '#FDF2F8' },
];

// Días esperados por período según la antigüedad. 18 días es el tope máximo interno:
// no hay tramo superior por antigüedad. Antes de 1 año no corresponde ningún día.
const diasEsperadosPorAntiguedad = (years) => {
  if (years < 1) return null;
  if (years < 5) return 14;
  return 18;
};

const periodoLabel = (anio) => `${anio - 1}-${String(anio).slice(-2)}`;

const fmt = {
  date: (d) => {
    if (!d) return '-';
    return new Date(d.slice(0,10) + 'T00:00:00')
      .toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' });
  },
  salary: (s) => {
    if (s == null || s === '') return '-';
    return 'RD$ ' + Number(s).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  },
  antiguedad: (fi, refDate = new Date()) => {
    if (!fi) return '';
    const ms = refDate - new Date(fi.slice(0,10) + 'T00:00:00');
    if (ms < 0) return 'Pendiente';
    const y = Math.floor(ms / (365.25 * 864e5));
    const m = Math.floor((ms / (365.25 * 864e5) - y) * 12);
    return y > 0 ? `${y}a ${m}m` : `${m}m`;
  },
};

// El diagnóstico se calcula SIEMPRE a partir de la fecha de ingreso y de los balances
// realmente cargados. No se acepta un texto de diagnóstico precalculado: los balances se
// están cargando manualmente y un diagnóstico guardado quedaría desfasado de los datos.
const calcDiagnostico = (emp) => {
  if (!emp.FechaIngreso) return { tipo: 'sin_datos', texto: 'Sin fecha de ingreso' };

  const ingreso = new Date(emp.FechaIngreso.slice(0,10) + 'T00:00:00');
  const balances = Array.isArray(emp.Balances) ? emp.Balances : [];
  const antiguedadHoy = (new Date() - ingreso) / (365.25 * 864e5);

  if (balances.length === 0) {
    return antiguedadHoy >= 1
      ? { tipo: 'deficit', texto: 'Sin balance registrado (ya cumplió 1 año)' }
      : { tipo: 'nuevo', texto: 'Sin balance — aún no cumple 1 año' };
  }

  const alertas = [];

  for (const b of balances) {
    const refDate = new Date(b.Anio, 11, 31);
    const years = (refDate - ingreso) / (365.25 * 864e5);
    const asignados = b.DiasTotales;
    if (asignados == null) continue;
    const esperados = diasEsperadosPorAntiguedad(years);
    if (esperados == null) { alertas.push({ tipo: 'nuevo', msg: `Menos de 1 anio al ${b.Anio}` }); continue; }
    if (asignados > esperados)
      alertas.push({ tipo: 'exceso',  msg: `Exceso ${periodoLabel(b.Anio)}: ${asignados}d / max ${esperados}d` });
    else if (asignados < esperados)
      alertas.push({ tipo: 'deficit', msg: `Deficit ${periodoLabel(b.Anio)}: ${asignados}d / min ${esperados}d` });
  }

  if (alertas.length === 0) return { tipo: 'ok', texto: 'Correcto (Art. 177)' };
  const exceso  = alertas.filter(a => a.tipo === 'exceso');
  const deficit = alertas.filter(a => a.tipo === 'deficit');
  if (exceso.length)  return { tipo: 'exceso',  texto: exceso.map(a => a.msg).join(' | ') };
  if (deficit.length) return { tipo: 'deficit', texto: deficit.map(a => a.msg).join(' | ') };
  return { tipo: 'nuevo', texto: alertas.map(a => a.msg).join(' | ') };
};

// ─── Sub-componentes ──────────────────────────────────────────────────────────
const Badge = ({ tipo, texto }) => {
  const paleta = {
    ok:       { bg: '#ECFDF5', color: '#059669', border: '#6EE7B7' },
    exceso:   { bg: '#FFFBEB', color: '#B45309', border: '#FDE68A' },
    deficit:  { bg: '#FEF2F2', color: '#DC2626', border: '#FECACA' },
    nuevo:    { bg: '#EFF6FF', color: '#2563EB', border: '#BFDBFE' },
    sin_datos:{ bg: '#F8FAFC', color: '#94A3B8', border: '#E2E8F0' },
    info:     { bg: 'rgba(0,200,212,.1)', color: '#00A8B4', border: 'rgba(0,200,212,.3)' },
  };
  const p = paleta[tipo] || paleta.info;
  return (
    <span style={{
      background: p.bg, color: p.color, border: `1px solid ${p.border}`,
      padding: '3px 9px', borderRadius: 20,
      fontSize: '.7rem', fontWeight: 700,
      whiteSpace: 'nowrap', display: 'inline-block', lineHeight: '1.4',
    }}>
      {texto}
    </span>
  );
};

const StatCard = ({ label, val, accent }) => (
  <div style={{
    background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10,
    padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,.06)', flexShrink: 0,
  }}>
    <div style={{
      width: 10, height: 36, borderRadius: 4, background: accent, flexShrink: 0,
    }} />
    <div>
      <div style={{ fontSize: '1.35rem', fontWeight: 700, color: C.text, lineHeight: 1 }}>{val ?? '...'}</div>
      <div style={{ fontSize: '.68rem', color: C.muted, marginTop: 2 }}>{label}</div>
    </div>
  </div>
);

const Spinner = ({ dark }) => (
  <div style={{
    width: 18, height: 18,
    border: `2px solid ${dark ? C.border : 'rgba(255,255,255,.3)'}`,
    borderTop: `2px solid ${dark ? C.teal : '#fff'}`,
    borderRadius: '50%',
    animation: 'spin .65s linear infinite', display: 'inline-block',
  }} />
);

const FieldLabel = ({ children }) => (
  <label style={{
    fontSize: '.68rem', fontWeight: 700, color: C.muted,
    textTransform: 'uppercase', letterSpacing: '.06em',
  }}>{children}</label>
);

const FieldInput = ({ value, onChange, type = 'text', disabled, placeholder, min }) => (
  <input
    type={type} value={value ?? ''} onChange={e => onChange?.(e.target.value)}
    disabled={disabled} placeholder={placeholder} min={min}
    style={{
      padding: '8px 11px', border: `1px solid ${C.border}`, borderRadius: 8,
      fontSize: '.85rem', color: C.text,
      background: disabled ? '#F1F5F9' : '#F8FAFC',
      outline: 'none', width: '100%', fontFamily: 'inherit',
      cursor: disabled ? 'not-allowed' : 'text',
      transition: 'border-color .15s, box-shadow .15s',
    }}
    onFocus={e => { if (!disabled) { e.target.style.borderColor = C.teal; e.target.style.boxShadow = `0 0 0 3px rgba(0,200,212,.1)`; }}}
    onBlur={e => { e.target.style.borderColor = C.border; e.target.style.boxShadow = ''; }}
  />
);

// Estilo de input compartido por los SearchableSelect (Direccion, Departamento,
// Supervisor) para que luzcan igual que los FieldInput/FieldSelect de al lado.
const selectInputStyle = {
  padding: '8px 11px', border: `1px solid ${C.border}`, borderRadius: 8,
  fontSize: '.85rem', color: C.text, background: '#F8FAFC',
  outline: 'none', width: '100%', fontFamily: 'inherit', boxSizing: 'border-box',
};

const FieldSelect = ({ value, onChange, children, disabled }) => (
  <select
    value={value ?? ''} onChange={e => onChange?.(e.target.value)} disabled={disabled}
    style={{
      padding: '8px 28px 8px 11px', border: `1px solid ${C.border}`, borderRadius: 8,
      fontSize: '.85rem', color: C.text, background: '#F8FAFC',
      outline: 'none', width: '100%', fontFamily: 'inherit', cursor: 'pointer',
      WebkitAppearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748B' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center',
    }}
  >{children}</select>
);

const SectionDivider = ({ children }) => (
  <div style={{
    fontSize: '.68rem', fontWeight: 700, letterSpacing: '.09em',
    textTransform: 'uppercase', color: C.teal2,
    borderBottom: `1px solid ${C.border}`, paddingBottom: 6, marginTop: 4,
  }}>{children}</div>
);

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
const PanelAvanzadoTH = () => {
  const [tab, setTab]           = useState('empleados');
  const [empleados, setEmpleados] = useState([]);
  const [catalogos, setCatalogos] = useState({ departamentos: [], direcciones: [], supervisores: [], puestos: [] });
  const [loading, setLoading]   = useState(false);
  const [toast, setToast]       = useState(null);
  const [error, setError]       = useState(null);

  const [search, setSearch]           = useState('');
  const [filterEstado, setFilterEstado] = useState('activos');
  const [filterDir, setFilterDir]     = useState('');
  const [filterDpto, setFilterDpto]   = useState('');
  const [filterDiag, setFilterDiag]   = useState('');

  const [editModal, setEditModal] = useState(false);
  const [editData, setEditData]   = useState({});
  const [saving, setSaving]       = useState(false);

  // ── Balance de Permisos (permiso personal / cumpleaños / médico / licencia) ──
  const [permisos, setPermisos]         = useState([]);
  const [permisosLoading, setPermisosLoading] = useState(false);
  const [ajusteModal, setAjusteModal]   = useState(null); // { empleadoID, nombreCompleto }
  const [ajusteForm, setAjusteForm]     = useState({ tipoPermiso: 'permiso_personal', dias: '', operacion: 'descuento', observaciones: '' });
  const [ajusteSaving, setAjusteSaving] = useState(false);

  const TIPOS_PERMISO = [
    { value: 'permiso_personal', label: 'Permiso Personal' },
    { value: 'cumpleanos',       label: 'Cumpleaños' },
    { value: 'medico',           label: 'Médico' },
    { value: 'licencia',         label: 'Licencia' },
  ];

  const showToast = useCallback((type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4800);
  }, []);

  // Normaliza el array Balances y deriva el total REALMENTE disponible
  // (no el total asignado), sin importar cuántos períodos tenga cada
  // empleado (0, 3, o 10 — ya no hay un número fijo).
  // DiasDisponibles es una columna calculada en SQL Server
  // ((DiasTotales - DiasUsados) - DiasPendientes); si por algún motivo no
  // viene del backend, se recalcula igual aquí como respaldo.
  const withBalancesComputadas = (emp) => {
    const balances = Array.isArray(emp.Balances) ? emp.Balances : [];
    const total = balances.reduce((sum, b) => {
      const disp = b.DiasDisponibles ?? ((b.DiasTotales || 0) - (b.DiasUsados || 0) - (b.DiasPendientes || 0));
      return sum + disp;
    }, 0);
    return { ...emp, Balances: balances, TotalDiasDisponibles: total || null };
  };

  // ── Cargar datos ──────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const h = { Authorization: `Bearer ${getToken()}` };

      const rConsolidado = await fetch(`${API_BASE_URL}/empleados/panel-consolidado`, { headers: h });

      if (rConsolidado.ok) {
        const data = await rConsolidado.json();
        setEmpleados(Array.isArray(data) ? data.map(withBalancesComputadas) : []);
      } else {
  // Fallback: usa balances-todos que ya funciona
  const [empRes, balances] = await Promise.all([
    fetch(`${API_BASE_URL}/empleados/list`, { headers: h }).then(r => r.json()),
    fetch(`${API_BASE_URL}/vacaciones/balances-todos`, { headers: h }).then(r => r.json()).catch(() => []),
  ]);

  const emps = Array.isArray(empRes) ? empRes : (empRes.empleados || empRes.data || []);
  const bal  = Array.isArray(balances) ? balances : [];

  const data = emps.map(emp => {
    const id = emp.EmpleadoID;
    const Balances = bal
      .filter(b => b.EmpleadoID === id)
      .map(b => ({
        BalanceID: b.BalanceID, Anio: b.Anio,
        DiasTotales: b.DiasTotales, DiasUsados: b.DiasUsados,
        DiasPendientes: b.DiasPendientes, DiasDisponibles: b.DiasDisponibles,
      }));
    return {
      ...emp,
      FechaIngreso:     emp.FechaIngreso     || emp.FECHAINGRESO         || null,
      SupervisorNombre: emp.SupervisorNombre  || emp.SupervisorInmediato  || null,
      Direccion:        emp.Direccion         || emp.NombreDireccion      || null,
      Departamento:     emp.Departamento      || emp.DEPARTAMENTO_NOMBRE  || emp.NombreDepartamento || null,
      NombreCompleto:   emp.NombreCompleto    || `${emp.NOMBRE || ''} ${emp.APELLIDO || ''}`.trim(),
      Balances,
    };
  });
  setEmpleados(data.map(withBalancesComputadas));
}
    } catch (e) {
      setError('Error cargando datos: ' + e.message);
      showToast('error', 'Error cargando datos: ' + e.message);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  // ── Cargar catalogos ──────────────────────────────────────────────────────
  const loadCatalogos = useCallback(async () => {
    try {
      const h = { Authorization: `Bearer ${getToken()}` };
      const [catRes, supsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/empleados/catalogos`, { headers: h }).then(r => r.ok ? r.json() : null).catch(() => null),
        fetch(`${API_BASE_URL}/empleados/list`,      { headers: h }).then(r => r.ok ? r.json() : []).catch(() => []),
      ]);

      // El endpoint /catalogos de tu backend devuelve { departamentos, direcciones, puestos }
      const deps = catRes?.departamentos || [];
      const dirs = catRes?.direcciones   || [];
      const psts = catRes?.puestos       || [];

      const supsArr = Array.isArray(supsRes) ? supsRes : (supsRes?.empleados || supsRes?.data || []);
      const sups = supsArr
        .filter(e => e.ESTADO == 1 || e.Estatus === 'Activo')
        .map(e => ({
          id:     e.EmpleadoID,
          nombre: e.NombreCompleto || `${e.NOMBRE || ''} ${e.APELLIDO || ''}`.trim(),
          cargo:  e.CARGO || '',
        }));

      setCatalogos({
        departamentos: deps.map(d => ({ id: d.DepartamentoID || d.id, nombre: d.NombreDepartamento || d.nombre })),
        direcciones:   dirs.map(d => ({ id: d.DireccionID    || d.id, nombre: d.NombreDireccion   || d.nombre })),
        supervisores:  sups,
        puestos:       psts.map(p => ({ id: p.PuestoID || p.id, nombre: p.Nombre || p.nombre })),
      });
    } catch (e) {
      console.error('Error cargando catalogos:', e);
    }
  }, []);

  // ── Cargar balance de permisos (todos los empleados, año actual) ──────────
  const loadPermisos = useCallback(async () => {
    setPermisosLoading(true);
    try {
      const h = { Authorization: `Bearer ${getToken()}` };
      const anio = new Date().getFullYear();
      const res = await fetch(`${API_BASE_URL}/permisos/balance?anio=${anio}`, { headers: h });
      const data = await res.json();
      setPermisos(Array.isArray(data) ? data : []);
    } catch (e) {
      showToast('error', 'Error cargando permisos: ' + e.message);
    } finally {
      setPermisosLoading(false);
    }
  }, [showToast]);

  useEffect(() => { loadData(); loadCatalogos(); loadPermisos(); }, [loadData, loadCatalogos, loadPermisos]);

  // ── Permisos agrupados por empleado (una fila por empleado, columnas por tipo) ──
  const permisosPorEmpleado = useMemo(() => {
    const porEmpleado = new Map();
    for (const p of permisos) {
      if (!porEmpleado.has(p.EmpleadoID)) {
        porEmpleado.set(p.EmpleadoID, { empleadoID: p.EmpleadoID, nombre: p.NombreCompleto, cargo: p.CARGO, tipos: {} });
      }
      porEmpleado.get(p.EmpleadoID).tipos[p.TipoPermiso] = p;
    }
    return Array.from(porEmpleado.values()).sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
  }, [permisos]);

  const permisosFiltrados = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return permisosPorEmpleado;
    return permisosPorEmpleado.filter(p => (p.nombre || '').toLowerCase().includes(q) || (p.cargo || '').toLowerCase().includes(q));
  }, [permisosPorEmpleado, search]);

  const abrirAjuste = (empleadoID, nombre) => {
    setAjusteModal({ empleadoID, nombre });
    setAjusteForm({ tipoPermiso: 'permiso_personal', dias: '', operacion: 'descuento', observaciones: '' });
  };

  const guardarAjuste = async () => {
    if (!ajusteForm.dias || Number(ajusteForm.dias) <= 0) {
      showToast('error', 'Ingresa una cantidad de días válida');
      return;
    }
    setAjusteSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/permisos/balance/ajuste`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          empleadoID: ajusteModal.empleadoID,
          tipoPermiso: ajusteForm.tipoPermiso,
          dias: ajusteForm.dias,
          operacion: ajusteForm.operacion,
          observaciones: ajusteForm.observaciones,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Error al aplicar el ajuste');
      showToast('success', data.message || 'Ajuste aplicado correctamente');
      setAjusteModal(null);
      await loadPermisos();
    } catch (e) {
      showToast('error', e.message);
    } finally {
      setAjusteSaving(false);
    }
  };

  // ── Períodos (columnas dinámicas) ────────────────────────────────────────
  // Se derivan de los años que realmente existen en los balances cargados,
  // en vez de una lista fija de 3 períodos (2023-24/2024-25/2025-26).
  const periodos = useMemo(() => {
    const anios = new Set();
    empleados.forEach(e => (e.Balances || []).forEach(b => anios.add(b.Anio)));
    return Array.from(anios).sort((a, b) => a - b).map((anio, i) => ({
      anio,
      label: periodoLabel(anio),
      ...PERIOD_PALETTE[i % PERIOD_PALETTE.length],
    }));
  }, [empleados]);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const activos   = empleados.filter(e => e.ESTADO == 1 || e.Estatus === 'Activo').length;
    const conExceso = empleados.filter(e => calcDiagnostico(e).tipo === 'exceso').length;
    const conDef    = empleados.filter(e => calcDiagnostico(e).tipo === 'deficit').length;
    return { activos, inactivos: empleados.length - activos, conExceso, conDef, total: empleados.length };
  }, [empleados]);

  // ── Filtrado ──────────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return empleados.filter(e => {
      const activo  = e.ESTADO == 1 || e.Estatus === 'Activo';
      const mEstado = filterEstado === 'todos' ? true : filterEstado === 'activos' ? activo : !activo;
      const mQ = !q
        || (e.NombreCompleto || '').toLowerCase().includes(q)
        || (e.CARGO || '').toLowerCase().includes(q)
        || String(e.EmpleadoID).includes(q)
        || (e.SupervisorNombre || '').toLowerCase().includes(q);
      const mDir  = !filterDir  || String(e.DireccionID)    === filterDir;
      const mDpto = !filterDpto || String(e.DEPARTAMENTOID) === filterDpto;
      const mDiag = !filterDiag || calcDiagnostico(e).tipo  === filterDiag;
      return mEstado && mQ && mDir && mDpto && mDiag;
    });
  }, [empleados, search, filterEstado, filterDir, filterDpto, filterDiag]);

  const openEdit = (emp) => {
    // ESTADO llega desde SQL Server como booleano (true/false) via mssql,
    // pero el <select> de Estado compara contra 1/0 — sin normalizar aqui,
    // el navegador no encuentra ninguna <option> que calce y muestra la
    // primera ("Activo") sin importar el valor real, aunque el filtro de
    // la lista (que sí usa == 1) siga clasificando correctamente al
    // empleado como inactivo. Eso hacia parecer un bug de "activo pero
    // sale como inactivo" cuando en realidad era solo de renderizado.
    setEditData({ ...emp, ESTADO: Number(emp.ESTADO) || 0, Balances: (emp.Balances || []).map(b => ({ ...b })) });
    setEditModal(true);
  };

  // Actualiza un campo (DiasTotales o DiasUsados) de un balance específico
  // dentro de editData.Balances, identificándolo por Anio.
  const updateBalanceField = (anio, field, value) => {
    setEditData(prev => ({
      ...prev,
      Balances: (prev.Balances || []).map(b => b.Anio === anio ? { ...b, [field]: value } : b),
    }));
  };

  // ── Guardar ───────────────────────────────────────────────────────────────
  const saveEdit = async () => {
    setSaving(true);
    try {
      const rEmp = await fetch(`${API_BASE_URL}/empleados/update/${editData.EmpleadoID}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
            nombre:              editData.NOMBRE || editData.Nombre,
            apellido:            editData.APELLIDO || editData.Apellido,
            cargo:               editData.CARGO,
            puestoID:            editData.PUESTOID || null,
            salario:             editData.Salario,
            fechaIngreso:        editData.FechaIngreso?.slice?.(0, 10),
            estado:              editData.ESTADO,
            departamentoID:      editData.DEPARTAMENTOID || null,   // ← D mayúscula
            direccionID:         editData.DireccionID || null,
            supervisorID:        editData.SupervisorID || null,      // ← D mayúscula; el nombre lo calcula el backend
            fechaSalida:         editData.FechaSalida,
            motivoSalida:        editData.MotivoSalida,
            }),
      });
      if (!rEmp.ok) {
        const err = await rEmp.json().catch(() => ({ error: 'Error al guardar empleado' }));
        throw new Error(err.error || err.message || 'Error al guardar empleado');
      }

      // Actualizar balances de vacaciones — recorre los balances reales del
      // empleado (pueden ser 0, 3, o cualquier cantidad), no una lista fija.
      const vacUpdates = [];
      for (const b of (editData.Balances || [])) {
        if (b.BalanceID && (b.DiasTotales !== undefined || b.DiasUsados !== undefined)) {
          vacUpdates.push(
            fetch(`${API_BASE_URL}/vacaciones/${b.BalanceID}`, {
              method: 'PUT',
              headers: authHeaders(),
              body: JSON.stringify({ DiasTotales: b.DiasTotales, DiasUsados: b.DiasUsados }),
            })
          );
        }
      }
      if (vacUpdates.length) await Promise.all(vacUpdates);

      showToast('success', 'Cambios guardados correctamente');
      setEditModal(false);
      await loadData();
    } catch (e) {
      showToast('error', e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Estilos base ──────────────────────────────────────────────────────────
  const tdBase = {
    padding: '10px 12px', fontSize: '.82rem', color: C.text,
    borderBottom: '1px solid #F1F5F9', verticalAlign: 'middle',
  };
  const thBase = {
    padding: '10px 12px', background: '#F8FAFC', borderBottom: `1px solid ${C.border}`,
    fontSize: '.66rem', fontWeight: 700, color: C.muted,
    textTransform: 'uppercase', letterSpacing: '.06em', textAlign: 'left', whiteSpace: 'nowrap',
  };
  const filterSelectStyle = {
    padding: '6px 28px 6px 10px', border: `1px solid ${C.border}`, borderRadius: 8,
    background: '#fff', fontSize: '.82rem', color: C.text, outline: 'none', cursor: 'pointer',
    WebkitAppearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748B' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', minWidth: 140,
  };

  return (
    <div style={{ fontFamily: "'Outfit','Inter',sans-serif", background: C.bg, minHeight: '100%', fontSize: 14 }}>

      <style>{`
        @keyframes spin      { to { transform: rotate(360deg); } }
        @keyframes toast-in  { from { transform: translateY(12px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes modal-in  { from { transform: scale(.96) translateY(8px); opacity: 0; } to { transform: scale(1) translateY(0); opacity: 1; } }
        .th-row:hover td { background: #F8FAFC !important; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 10px; }
      `}</style>

      {/* Header */}
      <div style={{ background: C.navy, padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,.06)' }}>
        <div>
          <div style={{ color: '#fff', fontSize: '1.05rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ background: 'rgba(0,200,212,.15)', color: C.teal, padding: '4px 10px', borderRadius: 6, fontSize: '.75rem', fontWeight: 600, letterSpacing: '.05em' }}>
              PANEL AVANZADO
            </span>
            Talento Humano y Servicios
          </div>
          <div style={{ color: 'rgba(255,255,255,.4)', fontSize: '.72rem', marginTop: 4 }}>
            ProDominicana RRHH · Modulo de Administracion de Personal
          </div>
        </div>
        <button
          onClick={loadData} disabled={loading}
          style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.1)', color: loading ? C.muted : '#fff', padding: '7px 14px', borderRadius: 8, fontSize: '.82rem', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'inherit', transition: 'all .15s' }}
        >
          {loading ? <Spinner /> : null} {loading ? 'Cargando...' : 'Recargar'}
        </button>
      </div>

      {/* Tabs */}
      <div style={{ background: C.navy2, display: 'flex', gap: 4, padding: '10px 24px 0', borderBottom: '1px solid rgba(255,255,255,.05)' }}>
        {[
          { id: 'empleados',  label: 'Directorio de Empleados' },
          { id: 'vacaciones', label: 'Balance de Vacaciones' },
          { id: 'permisos',   label: 'Balance de Permisos' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '9px 22px', borderRadius: '8px 8px 0 0', border: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: '.83rem', fontFamily: 'inherit', transition: 'all .15s',
            background: tab === t.id ? C.bg : 'transparent',
            color: tab === t.id ? C.text : 'rgba(255,255,255,.45)',
          }}>{t.label}</button>
        ))}
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 12, padding: '14px 24px 0', flexWrap: 'wrap' }}>
        <StatCard label="Empleados activos"  val={stats.activos}   accent={C.teal}   />
        <StatCard label="Inactivos"           val={stats.inactivos} accent={C.danger} />
        {tab === 'vacaciones' && <>
          <StatCard label="Con exceso dias"  val={stats.conExceso} accent={C.warn}   />
          <StatCard label="Con deficit"      val={stats.conDef}    accent={C.danger} />
        </>}
        <StatCard label="Total empleados"    val={stats.total}     accent={C.info}   />
        <StatCard label="Resultados"         val={filtered.length} accent={C.muted}  />
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, padding: '14px 24px 0', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative' }}>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar empleado, cargo, supervisor..."
            style={{ padding: '7px 12px 7px 12px', border: `1px solid ${C.border}`, borderRadius: 8, fontSize: '.85rem', color: C.text, background: '#fff', outline: 'none', width: 260, fontFamily: 'inherit' }}
          />
        </div>
        <select style={filterSelectStyle} value={filterEstado} onChange={e => setFilterEstado(e.target.value)}>
          <option value="activos">Solo activos</option>
          <option value="inactivos">Solo inactivos</option>
          <option value="todos">Todos</option>
        </select>
        <select style={filterSelectStyle} value={filterDir} onChange={e => setFilterDir(e.target.value)}>
          <option value="">Todas las direcciones</option>
          {catalogos.direcciones.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
        </select>
        <select style={filterSelectStyle} value={filterDpto} onChange={e => setFilterDpto(e.target.value)}>
          <option value="">Todos los dptos.</option>
          {catalogos.departamentos.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
        </select>
        {tab === 'vacaciones' && (
          <select style={filterSelectStyle} value={filterDiag} onChange={e => setFilterDiag(e.target.value)}>
            <option value="">Todos los diagnosticos</option>
            <option value="exceso">Con exceso</option>
            <option value="deficit">Con deficit</option>
            <option value="ok">Correctos</option>
            <option value="nuevo">Nuevo (menos de 1 anio)</option>
            <option value="sin_datos">Sin datos</option>
          </select>
        )}
        <span style={{ fontSize: '.73rem', color: C.muted, marginLeft: 'auto', fontStyle: 'italic' }}>
          Clic en una fila para editar
        </span>
      </div>

      {/* Error banner */}
      {error && (
        <div style={{ margin: '12px 24px 0', padding: '10px 14px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, fontSize: '.82rem', color: '#DC2626' }}>
          {error}
        </div>
      )}

      {/* Contenido */}
      <div style={{ padding: '12px 24px 32px' }}>

        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 12, color: C.muted }}>
            <Spinner dark />
            Cargando datos...
          </div>
        )}

        {/* TAB EMPLEADOS */}
        {!loading && tab === 'empleados' && (
          <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,.07)', overflow: 'hidden', marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: '.78rem', color: C.muted }}>{filtered.length} empleados</span>
              <span style={{ fontSize: '.72rem', color: C.muted }}>Datos personales y organizacionales</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {['ID','Nombre Completo','Cargo','Supervisor','Direccion','Departamento','Fecha Ingreso','Antiguedad','Salario','Estado'].map(h => (
                      <th key={h} style={thBase}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={10} style={{ ...tdBase, textAlign: 'center', padding: 50, color: C.muted }}>Sin resultados</td></tr>
                  ) : filtered.map(emp => {
                    const activo = emp.ESTADO == 1 || emp.Estatus === 'Activo';
                    return (
                      <tr key={emp.EmpleadoID} className="th-row" onClick={() => openEdit(emp)} style={{ cursor: 'pointer', transition: 'background .1s' }}>
                        <td style={{ ...tdBase, color: C.muted, fontSize: '.75rem', fontWeight: 700 }}>{emp.EmpleadoID}</td>
                        <td style={{ ...tdBase, fontWeight: 700, whiteSpace: 'nowrap' }}>{emp.NombreCompleto}</td>
                        <td style={{ ...tdBase, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={emp.CARGO}>{emp.CARGO || '-'}</td>
                        <td style={{ ...tdBase, fontSize: '.78rem', whiteSpace: 'nowrap' }}>{emp.SupervisorNombre || '-'}</td>
                        <td style={{ ...tdBase, fontSize: '.78rem' }}>{emp.Direccion || '-'}</td>
                        <td style={{ ...tdBase, fontSize: '.78rem' }}>{emp.Departamento || '-'}</td>
                        <td style={{ ...tdBase, fontSize: '.78rem', whiteSpace: 'nowrap' }}>{fmt.date(emp.FechaIngreso)}</td>
                        <td style={tdBase}>
                          {emp.FechaIngreso && activo
                            ? <Badge tipo="info" texto={fmt.antiguedad(emp.FechaIngreso)} />
                            : <span style={{ color: C.muted }}>-</span>}
                        </td>
                        <td style={{ ...tdBase, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmt.salary(emp.Salario)}</td>
                        <td style={tdBase}>
                          <Badge tipo={activo ? 'ok' : 'deficit'} texto={activo ? 'Activo' : 'Inactivo'} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB VACACIONES */}
        {!loading && tab === 'vacaciones' && (
          <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,.07)', overflow: 'hidden', marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: '.78rem', color: C.muted }}>{filtered.length} empleados · Balances multi-periodo</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thBase}>ID</th>
                    <th style={thBase}>Empleado</th>
                    <th style={thBase}>Cargo</th>
                    <th style={thBase}>Ingreso</th>
                    <th style={thBase}>Antiguedad</th>
                    {periodos.map(p => (
                      <th key={p.anio} colSpan={2} style={{ ...thBase, borderLeft: `2px solid ${p.color}33`, background: p.bg, color: p.color, textAlign: 'center' }}>{p.label}</th>
                    ))}
                    <th style={{ ...thBase, borderLeft: '2px solid #E2E8F0' }}>Total</th>
                    <th style={thBase}>Diagnostico</th>
                  </tr>
                  <tr>
                    {['','','','',''].map((_, i) => <th key={i} style={{ ...thBase, padding: '4px 12px', fontSize: '.6rem' }}></th>)}
                    {periodos.map(p => (
                      <React.Fragment key={p.anio}>
                        <th style={{ ...thBase, padding: '4px 12px', fontSize: '.6rem', borderLeft: `2px solid ${p.color}22`, background: p.bg, color: p.color }}>Total</th>
                        <th style={{ ...thBase, padding: '4px 12px', fontSize: '.6rem', background: p.bg, color: p.color }}>Usados</th>
                      </React.Fragment>
                    ))}
                    <th style={{ ...thBase, padding: '4px 12px', fontSize: '.6rem', borderLeft: '2px solid #E2E8F0' }}></th>
                    <th style={{ ...thBase, padding: '4px 12px', fontSize: '.6rem' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={5 + periodos.length * 2 + 2} style={{ ...tdBase, textAlign: 'center', padding: 50, color: C.muted }}>Sin resultados</td></tr>
                  ) : filtered.map(emp => {
                    const activo = emp.ESTADO == 1 || emp.Estatus === 'Activo';
                    const diag   = calcDiagnostico(emp);
                    return (
                      <tr key={emp.EmpleadoID} className="th-row" onClick={() => openEdit(emp)} style={{ cursor: 'pointer', transition: 'background .1s' }}>
                        <td style={{ ...tdBase, color: C.muted, fontSize: '.75rem', fontWeight: 700 }}>{emp.EmpleadoID}</td>
                        <td style={{ ...tdBase, fontWeight: 700, whiteSpace: 'nowrap' }}>{emp.NombreCompleto}</td>
                        <td style={{ ...tdBase, fontSize: '.78rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={emp.CARGO}>{emp.CARGO || '-'}</td>
                        <td style={{ ...tdBase, fontSize: '.78rem', whiteSpace: 'nowrap' }}>{fmt.date(emp.FechaIngreso)}</td>
                        <td style={tdBase}>
                          {emp.FechaIngreso && activo
                            ? <Badge tipo="info" texto={fmt.antiguedad(emp.FechaIngreso)} />
                            : <span style={{ color: C.muted }}>-</span>}
                        </td>
                        {periodos.map(p => {
                          const bal = (emp.Balances || []).find(b => b.Anio === p.anio);
                          return (
                            <React.Fragment key={p.anio}>
                              <td style={{ ...tdBase, borderLeft: `2px solid ${p.color}33`, fontWeight: 700, color: p.color, textAlign: 'center' }}>{bal?.DiasTotales ?? '-'}</td>
                              <td style={{ ...tdBase, color: C.muted, textAlign: 'center' }}>{bal?.DiasUsados ?? '-'}</td>
                            </React.Fragment>
                          );
                        })}
                        <td style={{ ...tdBase, borderLeft: `2px solid ${C.border}`, fontWeight: 800, fontSize: '1rem', textAlign: 'center', color: C.teal2 }}>
                          {emp.TotalDiasDisponibles > 0 ? emp.TotalDiasDisponibles : '-'}
                        </td>
                        <td style={{ ...tdBase, maxWidth: 440 }}>
                          <Badge tipo={diag.tipo} texto={diag.texto.slice(0, 110) + (diag.texto.length > 110 ? '...' : '')} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB PERMISOS */}
        {tab === 'permisos' && (
          <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,.07)', overflow: 'hidden', marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: '.78rem', color: C.muted }}>{permisosFiltrados.length} empleados · Permiso Personal y Cumpleaños del {new Date().getFullYear()}</span>
              <span style={{ fontSize: '.72rem', color: C.muted }}>Clic en "Ajustar" para descontar o acreditar días (médico/licencia incluidos ahí)</span>
            </div>
            {permisosLoading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 12, color: C.muted }}>
                <Spinner dark /> Cargando permisos...
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={thBase}>ID</th>
                      <th style={thBase}>Empleado</th>
                      <th style={thBase}>Cargo</th>
                      <th style={{ ...thBase, textAlign: 'center' }} colSpan={3}>Permiso Personal</th>
                      <th style={{ ...thBase, textAlign: 'center', borderLeft: `2px solid ${C.border}` }} colSpan={3}>Cumpleaños</th>
                      <th style={thBase}></th>
                    </tr>
                    <tr>
                      {['', '', ''].map((_, i) => <th key={i} style={{ ...thBase, padding: '4px 12px', fontSize: '.6rem' }}></th>)}
                      {['Total', 'Usados', 'Disp.'].map((h, i) => (
                        <th key={h} style={{ ...thBase, padding: '4px 12px', fontSize: '.6rem', textAlign: 'center', borderLeft: i === 0 ? `2px solid ${C.border}` : undefined }}>{h}</th>
                      ))}
                      {['Total', 'Usados', 'Disp.'].map((h, i) => (
                        <th key={h} style={{ ...thBase, padding: '4px 12px', fontSize: '.6rem', textAlign: 'center', borderLeft: i === 0 ? `2px solid ${C.border}` : undefined }}>{h}</th>
                      ))}
                      <th style={thBase}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {permisosFiltrados.length === 0 ? (
                      <tr><td colSpan={9} style={{ ...tdBase, textAlign: 'center', padding: 50, color: C.muted }}>Sin resultados</td></tr>
                    ) : permisosFiltrados.map(p => {
                      const personal = p.tipos['permiso_personal'];
                      const cumple = p.tipos['cumpleanos'];
                      const celda = (b) => b
                        ? <>
                            <td style={{ ...tdBase, textAlign: 'center', borderLeft: `2px solid ${C.border}` }}>{b.DiasTotales}</td>
                            <td style={{ ...tdBase, textAlign: 'center', color: C.muted }}>{b.DiasUsados}</td>
                            <td style={{ ...tdBase, textAlign: 'center', fontWeight: 700, color: b.DiasDisponibles > 0 ? C.teal2 : C.danger }}>{b.DiasDisponibles}</td>
                          </>
                        : <>
                            <td style={{ ...tdBase, textAlign: 'center', borderLeft: `2px solid ${C.border}`, color: C.muted }}>-</td>
                            <td style={{ ...tdBase, textAlign: 'center', color: C.muted }}>-</td>
                            <td style={{ ...tdBase, textAlign: 'center', color: C.muted }}>-</td>
                          </>;
                      return (
                        <tr key={p.empleadoID} className="th-row">
                          <td style={{ ...tdBase, color: C.muted, fontSize: '.75rem', fontWeight: 700 }}>{p.empleadoID}</td>
                          <td style={{ ...tdBase, fontWeight: 700, whiteSpace: 'nowrap' }}>{p.nombre}</td>
                          <td style={{ ...tdBase, fontSize: '.78rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.cargo}>{p.cargo || '-'}</td>
                          {celda(personal)}
                          {celda(cumple)}
                          <td style={tdBase}>
                            <button
                              onClick={() => abrirAjuste(p.empleadoID, p.nombre)}
                              style={{ padding: '5px 12px', borderRadius: 7, fontSize: '.75rem', fontWeight: 600, cursor: 'pointer', background: C.teal, color: '#fff', border: 'none', fontFamily: 'inherit' }}
                            >Ajustar</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* MODAL AJUSTE DE PERMISOS */}
      {ajusteModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20, backdropFilter: 'blur(3px)' }}
          onClick={e => e.target === e.currentTarget && setAjusteModal(null)}
        >
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440, boxShadow: '0 8px 40px rgba(0,0,0,.2)', animation: 'modal-in .2s ease' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: `1px solid ${C.border}` }}>
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: C.text }}>Ajustar Permisos</div>
                <div style={{ fontSize: '.78rem', color: C.muted, marginTop: 2 }}>{ajusteModal.nombre}</div>
              </div>
              <button onClick={() => setAjusteModal(null)} style={{ background: '#F1F5F9', border: 'none', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', fontSize: '1rem' }}>x</button>
            </div>
            <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <FieldLabel>Tipo de Permiso</FieldLabel>
                <FieldSelect value={ajusteForm.tipoPermiso} onChange={v => setAjusteForm(p => ({ ...p, tipoPermiso: v }))}>
                  {TIPOS_PERMISO.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </FieldSelect>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <FieldLabel>Operación</FieldLabel>
                  <FieldSelect value={ajusteForm.operacion} onChange={v => setAjusteForm(p => ({ ...p, operacion: v }))}>
                    <option value="descuento">Descontar (ya usó)</option>
                    <option value="credito">Acreditar (agregar días)</option>
                  </FieldSelect>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <FieldLabel>Días</FieldLabel>
                  <FieldInput type="number" min="0.5" placeholder="Ej: 1" value={ajusteForm.dias} onChange={v => setAjusteForm(p => ({ ...p, dias: v }))} />
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <FieldLabel>Observaciones</FieldLabel>
                <FieldInput placeholder="Ej: Tomó el día 12 de septiembre" value={ajusteForm.observaciones} onChange={v => setAjusteForm(p => ({ ...p, observaciones: v }))} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '14px 22px', borderTop: `1px solid ${C.border}` }}>
              <button onClick={() => setAjusteModal(null)} style={{ padding: '8px 16px', borderRadius: 8, fontSize: '.83rem', fontWeight: 600, cursor: 'pointer', background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, fontFamily: 'inherit' }}>Cancelar</button>
              <button
                onClick={guardarAjuste} disabled={ajusteSaving}
                style={{ padding: '8px 20px', borderRadius: 8, fontSize: '.83rem', fontWeight: 600, cursor: ajusteSaving ? 'not-allowed' : 'pointer', background: C.teal, color: '#fff', border: 'none', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 8, opacity: ajusteSaving ? .8 : 1 }}
              >
                {ajusteSaving ? <><Spinner /> Aplicando...</> : 'Aplicar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE EDICION */}
      {editModal && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20, backdropFilter: 'blur(3px)' }}
          onClick={e => e.target === e.currentTarget && setEditModal(false)}
        >
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 700, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,.2)', animation: 'modal-in .2s ease' }}>

            {/* Modal Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px', borderBottom: `1px solid ${C.border}`, position: 'sticky', top: 0, background: '#fff', zIndex: 1, borderRadius: '16px 16px 0 0' }}>
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: C.text }}>Editar Empleado</div>
                <div style={{ fontSize: '.78rem', color: C.muted, marginTop: 2 }}>ID {editData.EmpleadoID} · {editData.NombreCompleto}</div>
              </div>
              <button onClick={() => setEditModal(false)} style={{ background: '#F1F5F9', border: 'none', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>x</button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Alerta diagnostico */}
              {(() => {
                const d = calcDiagnostico(editData);
                if (d.tipo === 'ok' || d.tipo === 'sin_datos') return null;
                const bg = d.tipo === 'exceso' ? '#FFFBEB' : '#FEF2F2';
                const bc = d.tipo === 'exceso' ? '#FDE68A' : '#FECACA';
                const fc = d.tipo === 'exceso' ? '#92400E' : '#991B1B';
                return (
                  <div style={{ padding: '10px 14px', borderRadius: 8, background: bg, border: `1px solid ${bc}`, fontSize: '.8rem', color: fc, lineHeight: 1.5 }}>
                    <strong>Diagnostico detectado:</strong> {d.texto}
                  </div>
                );
              })()}

              <SectionDivider>Informacion Personal</SectionDivider>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <FieldLabel>Nombre</FieldLabel>
                  <FieldInput value={editData.NOMBRE || editData.Nombre} onChange={v => setEditData(p => ({ ...p, NOMBRE: v, Nombre: v }))} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <FieldLabel>Apellido</FieldLabel>
                  <FieldInput value={editData.APELLIDO || editData.Apellido} onChange={v => setEditData(p => ({ ...p, APELLIDO: v, Apellido: v }))} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <FieldLabel>Cargo / Puesto</FieldLabel>
                  <SearchableSelect
                    value={editData.PUESTOID}
                    onChange={v => setEditData(p => {
                      // El texto CARGO se mantiene en sincronia con el puesto elegido: varias
                      // pantallas del sistema siguen mostrando ese texto libre, no el PuestoID.
                      const puesto = catalogos.puestos.find(x => String(x.id) === String(v));
                      return { ...p, PUESTOID: v, CARGO: puesto ? puesto.nombre : p.CARGO };
                    })}
                    options={catalogos.puestos.map(p => ({ id: p.id, label: p.nombre }))}
                    placeholder="Buscar puesto..."
                    inputStyle={selectInputStyle}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <FieldLabel>Salario (RD$)</FieldLabel>
                  <FieldInput type="number" value={editData.Salario} onChange={v => setEditData(p => ({ ...p, Salario: v }))} placeholder="0.00" />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <FieldLabel>Fecha de Ingreso</FieldLabel>
                  <FieldInput type="date" value={editData.FechaIngreso?.slice?.(0, 10)} onChange={v => setEditData(p => ({ ...p, FechaIngreso: v }))} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <FieldLabel>Estado</FieldLabel>
                  <FieldSelect value={editData.ESTADO} onChange={v => setEditData(p => ({ ...p, ESTADO: Number(v) }))}>
                    <option value={1}>Activo</option>
                    <option value={0}>Inactivo</option>
                  </FieldSelect>
                </div>
              </div>

              {editData.ESTADO == 0 && (
                <>
                  <SectionDivider>Datos de Baja</SectionDivider>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <FieldLabel>Fecha de Salida</FieldLabel>
                      <FieldInput type="date" value={editData.FechaSalida?.slice?.(0, 10)} onChange={v => setEditData(p => ({ ...p, FechaSalida: v }))} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <FieldLabel>Motivo de Salida</FieldLabel>
                      <FieldInput value={editData.MotivoSalida} onChange={v => setEditData(p => ({ ...p, MotivoSalida: v }))} placeholder="Ej: Renuncia voluntaria" />
                    </div>
                  </div>
                </>
              )}

              <SectionDivider>Organizacion</SectionDivider>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <FieldLabel>Direccion</FieldLabel>
                  <SearchableSelect
                    value={editData.DireccionID}
                    onChange={v => setEditData(p => ({ ...p, DireccionID: v }))}
                    options={catalogos.direcciones.map(d => ({ id: d.id, label: d.nombre }))}
                    placeholder="Buscar direccion..."
                    inputStyle={selectInputStyle}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <FieldLabel>Departamento</FieldLabel>
                  <SearchableSelect
                    value={editData.DEPARTAMENTOID}
                    onChange={v => setEditData(p => ({ ...p, DEPARTAMENTOID: v }))}
                    options={catalogos.departamentos.map(d => ({ id: d.id, label: d.nombre }))}
                    placeholder="Buscar departamento..."
                    inputStyle={selectInputStyle}
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, gridColumn: '1 / -1' }}>
                  <FieldLabel>Supervisor Inmediato</FieldLabel>
                  <SearchableSelect
                    value={editData.SupervisorID}
                    onChange={v => setEditData(p => ({ ...p, SupervisorID: v }))}
                    options={catalogos.supervisores.map(s => ({ id: s.id, label: s.nombre + (s.cargo ? ` · ${s.cargo}` : '') }))}
                    placeholder="Buscar supervisor..."
                    emptyLabel="-- Sin supervisor --"
                    inputStyle={selectInputStyle}
                  />
                </div>
              </div>

              <SectionDivider>Balance de Vacaciones por Periodo</SectionDivider>

              {(editData.Balances || []).length === 0 && (
                <div style={{ fontSize: '.8rem', color: C.muted, fontStyle: 'italic', padding: '4px 0 8px' }}>
                  Este empleado no tiene balances de vacaciones registrados.
                </div>
              )}

              {(editData.Balances || []).slice().sort((a, b) => a.Anio - b.Anio).map((b, i) => {
                const palette = PERIOD_PALETTE[i % PERIOD_PALETTE.length];
                const label   = periodoLabel(b.Anio);
                const disp        = b.DiasTotales;
                const usados      = b.DiasUsados;
                const pendientes  = b.DiasPendientes || 0;
                const disponibleReal = (disp || 0) - (usados || 0) - pendientes;
                let esperados = null;
                if (editData.FechaIngreso) {
                  const ingreso = new Date(editData.FechaIngreso.slice(0,10) + 'T00:00:00');
                  const ref     = new Date(b.Anio, 11, 31);
                  const years   = (ref - ingreso) / (365.25 * 864e5);
                  esperados = diasEsperadosPorAntiguedad(years);
                }
                const alerta = disp != null && esperados != null && disp !== esperados;
                return (
                  <div key={b.Anio} style={{ background: alerta ? (disp > esperados ? '#FFFBEB' : '#FFF1F2') : '#F8FAFC', border: `1px solid ${alerta ? (disp > esperados ? '#FDE68A' : '#FECACA') : C.border}`, borderRadius: 10, padding: '12px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <span style={{ fontSize: '.73rem', fontWeight: 700, color: palette.color, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                        Periodo {label}
                      </span>
                      {alerta && (
                        <span style={{ fontSize: '.68rem', fontWeight: 700, color: disp > esperados ? '#B45309' : '#DC2626' }}>
                          {disp > esperados ? `Exceso (correspondia ${esperados}d)` : `Deficit (correspondia ${esperados}d)`}
                        </span>
                      )}
                      {!alerta && esperados != null && (
                        <span style={{ fontSize: '.68rem', color: C.ok, fontWeight: 600 }}>Correcto</span>
                      )}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <FieldLabel>Dias Totales Asignados</FieldLabel>
                        <FieldInput type="number" min="0" value={disp} onChange={v => updateBalanceField(b.Anio, 'DiasTotales', Number(v))} />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                        <FieldLabel>Dias Usados</FieldLabel>
                        <FieldInput type="number" min="0" value={usados} onChange={v => updateBalanceField(b.Anio, 'DiasUsados', Number(v))} />
                      </div>
                    </div>
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px dashed ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '.72rem', color: C.muted }}>
                        Disponible real (Totales − Usados{pendientes > 0 ? ` − ${pendientes} pendientes` : ''})
                      </span>
                      <span style={{ fontSize: '.9rem', fontWeight: 800, color: disponibleReal < 0 ? C.danger : C.teal2 }}>
                        {disponibleReal} dias
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Modal Footer */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '14px 22px', borderTop: `1px solid ${C.border}`, position: 'sticky', bottom: 0, background: '#fff', borderRadius: '0 0 16px 16px' }}>
              <button
                onClick={() => setEditModal(false)}
                style={{ padding: '8px 16px', borderRadius: 8, fontSize: '.83rem', fontWeight: 600, cursor: 'pointer', background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, fontFamily: 'inherit' }}
              >
                Cancelar
              </button>
              <button
                onClick={saveEdit} disabled={saving}
                style={{ padding: '8px 20px', borderRadius: 8, fontSize: '.83rem', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', background: C.teal, color: '#fff', border: 'none', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 8, opacity: saving ? .8 : 1, transition: 'all .15s' }}
              >
                {saving ? <><Spinner /> Guardando...</> : 'Guardar Cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, padding: '12px 20px', borderRadius: 10,
          fontSize: '.85rem', fontWeight: 600, boxShadow: '0 8px 32px rgba(0,0,0,.15)',
          zIndex: 200, display: 'flex', alignItems: 'center', gap: 8, animation: 'toast-in .25s ease',
          background: toast.type === 'success' ? '#ECFDF5' : '#FEF2F2',
          color:      toast.type === 'success' ? '#065F46' : '#991B1B',
          border:     `1px solid ${toast.type === 'success' ? '#A7F3D0' : '#FECACA'}`,
        }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default PanelAvanzadoTH;